import React, { useState, useEffect, useRef } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { Box, Typography, Paper, Chip, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import moment from 'moment';
import 'moment-timezone';
// Iconos
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import TicketDetailModal from './TicketDetailModal';

// Estilos personalizados
const TicketContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.25), // Aumentado ligeramente para dar más espacio interno
  marginBottom: theme.spacing(1), // Aumentado para dar más separación entre tickets
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
  '&:hover': {
    boxShadow: theme.shadows[3],
  },
  transition: 'box-shadow 0.2s ease-in-out',
  width: '100%', // Asegurar que ocupe todo el ancho disponible
  height: 'auto', // Altura automática basada en el contenido
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  minHeight: '80px', // Aumentado para dar más espacio al contenido
  maxHeight: '160px', // Aumentado para dar más espacio al contenido
}));

/**
 * Chip para mostrar la prioridad de un ticket con colores según el nivel
 */
const PriorityChip = styled(Chip)(({ priority, theme }) => {
  const getPriorityColor = () => {
    switch (priority) {
      case 'baja':
        return theme.palette.success.main;
      case 'media':
        return theme.palette.warning.main;
      case 'alta':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return {
    backgroundColor: getPriorityColor(),
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '0.7rem',
    height: '20px', // Reducido para ser más compacto
    '& .MuiChip-label': {
      padding: '0 6px', // Reducido el padding para ser más compacto
    }
  };
});

const InfoSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.1), // Reducido aún más para ser más compacto
  marginTop: theme.spacing(0.25), // Reducido para ser más compacto
  fontSize: '0.7rem', // Reducido para ser más compacto
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5), // Volvemos a 0.5 para ocupar menos espacio
  fontSize: '0.7rem', // Volvemos a 0.7 para mantener la consistencia
  width: '100%',
  justifyContent: 'space-between', // Volvemos a 'space-between' para mantener la consistencia
}));

/**
 * Extrae los metadatos de un ticket si están en formato JSON
 * @param {Object} ticket - El ticket de donde extraer los metadatos
 * @returns {Object} Objeto con los metadatos extraídos
 */
const extractMetadata = (ticket) => {
  // Valores predeterminados
  const defaultMetadata = {
    description: '',
    prioridad: 'media'
  };

  // Verificación extra para evitar errores con tickets inválidos
  if (!ticket || typeof ticket !== 'object') {
    console.warn('Ticket inválido en extractMetadata:', ticket);
    return defaultMetadata;
  }

  // Si no hay metadatos, buscar descripción directamente en el ticket
  if (!ticket.meta_data) {
    return {
      ...defaultMetadata,
      description: ticket.description || ''
    };
  }

  try {
    // Intentar parsear los metadatos si son una cadena JSON
    let metadata = ticket.meta_data;

    if (typeof ticket.meta_data === 'string') {
      try {
        metadata = JSON.parse(ticket.meta_data);
      } catch (parseError) {
        console.warn('Error al parsear metadatos JSON:', parseError, ticket.meta_data);
        metadata = {};
      }
    } else if (typeof ticket.meta_data !== 'object') {
      console.warn('meta_data no es un objeto ni una cadena JSON:', typeof ticket.meta_data);
      metadata = {};
    }

    // Combinar metadatos con valores predeterminados y descripción del ticket si existe
    return {
      ...defaultMetadata,
      ...metadata,
      // Dar prioridad a la descripción del ticket sobre la de los metadatos
      description: ticket.description || metadata.description || ''
    };
  } catch (error) {
    console.warn('Error al procesar metadatos del ticket:', error);
    return {
      ...defaultMetadata,
      description: ticket.description || ''
    };
  }
};

/**
 * Componente que representa un ticket en el tablero Kanban
 * @param {Object} ticket - Datos del ticket
 * @param {string|number} ticket.id - Identificador único del ticket
 * @param {string} ticket.name - Nombre o título del ticket
 * @param {string} ticket.description - Descripción del ticket (campo directo)
 * @param {string} ticket.meta_data - Metadatos en formato JSON que contienen prioridad y otros datos
 * @param {string} ticket.id_contact - ID del contacto asociado (ahora string)
 * @param {string} ticket.created_at - Fecha de creación del ticket
 * @param {string} ticket.created_by_agent - ID del creador (ahora string)
 * @param {string} ticket.assigned_to_agent - ID del agente asignado (ahora string)
 * @param {string} ticket.creator_agent_name - Nombre del agente creador
 * @param {string} ticket.assigned_agent_name - Nombre del agente asignado
 * @param {number} index - Posición del ticket en la columna
 * @param {Object} column - Columna donde se encuentra el ticket
 * @returns {JSX.Element} Componente de ticket
 */
const KanbanTicket = ({ ticket: initialTicket, index, column }) => {
  const isMountedRef = useRef(true);
  // Estado para controlar la visibilidad del modal de detalles
  const [modalOpen, setModalOpen] = useState(false);

  // Obtener el ticket actualizado del estado global de Redux
  // Adaptación a la nueva estructura del estado después de la refactorización
  const { tickets: { items: ticketItems = [] } = { items: [] } } = useSelector(state => state.kanban);

  // Buscar el ticket actualizado en el estado global
  const updatedTicket = ticketItems.find(t => t.id === initialTicket?.id) || initialTicket;

  // Usar el ticket actualizado para renderizar
  const ticket = updatedTicket;

  // Efecto para registrar cuando el ticket se actualiza
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (ticket && ticket.assigned_to_agent) {
      // Debug log eliminado
    }
  }, [ticket]);

  // Si el ticket es null o undefined, renderizar un placeholder
  if (!ticket) {
    console.error('Se recibió un ticket nulo o indefinido');
    return (
      <TicketContainer elevation={1} sx={{ bgcolor: '#ffebee' }}>
        <Typography variant="body2" color="error">
          Error: Ticket no válido
        </Typography>
      </TicketContainer>
    );
  }

  /**
   * Obtiene la fecha formateada del ticket en zona horaria de México
   * @param {boolean} [useUpdatedAt=false] - Si es true, usa la fecha de actualización, de lo contrario usa la de creación
   * @returns {Object} Objeto con la fecha formateada y si es de hoy
   */
  const getFormattedDate = (useUpdatedAt = false) => {
    const dateField = useUpdatedAt ? 'updated_at' : 'created_at';
    const formattedField = useUpdatedAt ? 'formatted_updated_at' : 'formatted_created_at';
    const isTodayField = useUpdatedAt ? 'is_updated_today' : 'is_created_today';
    
    // Si el ticket ya tiene los campos formateados, usarlos
    if (ticket[formattedField] !== undefined) {
      return {
        formattedDate: ticket[formattedField],
        isToday: ticket[isTodayField]
      };
    }
    
    // Si no, formatear la fecha manualmente usando zona horaria de México
    const date = ticket[dateField];
    if (!date) return { formattedDate: 'Fecha no disponible', isToday: false };
    
    // Convertir la fecha UTC directamente a zona horaria de México
    const momentDate = moment.utc(date).tz('America/Mexico_City');
    const todayStart = moment().tz('America/Mexico_City').startOf('day');
    const isToday = momentDate.isSameOrAfter(todayStart, 'second');
    
    const formattedDate = isToday 
      ? momentDate.format('LT')  // Solo hora si es hoy
      : momentDate.format('lll'); // Fecha y hora completa si no es hoy
    
    return {
      formattedDate,
      isToday
    };
  };

  // Extraer metadatos del ticket
  const metadata = extractMetadata(ticket);

  // Obtener información del ticket (compatible con ambos formatos)
  const ticketTitle = ticket.name || ticket.motivo || 'Sin título';
  
  // Obtener fechas formateadas
  const { formattedDate: createdDate } = getFormattedDate(false); // Fecha de creación
  const { formattedDate: updatedDate } = getFormattedDate(true);  // Fecha de actualización
  const ticketDescription = metadata.description || ticket.description || ticket.motivo || '';
  const ticketPriority = metadata.prioridad || ticket.prioridad || 'media';

  // Garantizar que ticketId sea siempre una cadena válida
  // Convertir cualquier valor a string y asegurar que no sea undefined o null
  const ticketId = ticket.id ? String(ticket.id).replace(/[^a-zA-Z0-9-_]/g, '') : `temp-${Math.random().toString(36).substring(2, 9)}`;

  // Si el ticket no tiene una propiedad 'description' definida, podemos registrarlo para depuración
  if (ticket && typeof ticket.description === 'undefined') {
    
  }

  /**
   * Manejador para abrir el modal de detalles
   * @param {Event} e - Evento del clic
   */
  const handleOpenModal = (e) => {
    // Prevenir que el evento se propague al Draggable
    e.stopPropagation();
    e.preventDefault();
    // Debug log eliminado
    setModalOpen(true);
  };

  /**
   * Manejador para cerrar el modal de detalles
   * @param {Object} updatedTicket - Ticket actualizado (opcional)
   */
  const handleCloseModal = (updatedTicket) => {
    // Debug log eliminado

    // Si recibimos un ticket actualizado, actualizar el estado local
    if (updatedTicket) {
  
    }

    // Cerrar el modal
    if (isMountedRef.current) {
      setModalOpen(false);
    }
  };

  /**
   * Renderiza la información del ticket
   * @returns {JSX.Element} Información del ticket
   */
  const renderTicketInfo = () => {
    // Si el ticket no existe, no renderizar nada
    if (!ticket) return null;

    // Obtener número de teléfono del contacto de todas las posibles fuentes
    const contactPhone = ticket.contact_phone ||
                        (ticket.contact && ticket.contact.phone) ||
                        (ticket.contactData && ticket.contactData.phone) ||
                        null;

    // Obtener nombre del contacto de todas las posibles fuentes
    const contactName = ticket.name_contact ||
                       ticket.contact_name ||
                       (ticket.contact && ticket.contact.name) ||
                       (ticket.contactData && ticket.contactData.name) ||
                       null;

    // Prioridad: nombre > teléfono > ID del contacto > mensaje genérico
    const displayContactName = contactName || 
                              String(contactPhone) || 
                              (ticket.id_contact ? `Contacto #${ticket.id_contact}` : 'Contacto sin ID');

    // Obtener nombre del agente de todas las posibles fuentes
    const agentName = ticket.assigned_agent_name ||
                     ticket.name_agent ||
                     ticket.assigned_name ||
                     (ticket.agent && ticket.agent.name) ||
                     (ticket.agentData && ticket.agentData.name) ||
                     (ticket.assigned_to_agent ? `Agente #${ticket.assigned_to_agent}` : 'Sin asignar');


    return (
      <InfoSection>

        {/* Fecha de creación */}
        <InfoRow>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CalendarTodayIcon sx={{ fontSize: 10, color: 'text.secondary', mr: 0.25 }} />
            <Tooltip title={`Actualizado: ${updatedDate}`}>
              <Typography variant="caption" color="text.secondary" noWrap>
                {createdDate}
              </Typography>
            </Tooltip>
          </Box>
        </InfoRow>

        {/* Información del contacto - Mostramos el nombre o teléfono, con tooltip del ID */}
        <InfoRow>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <PersonIcon sx={{ fontSize: 10, color: 'primary.main', mr: 0.25 }} />
            <Tooltip title={`ID: ${ticket.id_contact || 'N/A'}${contactPhone && contactName ? ` - Tel: ${contactPhone}` : ''}`}>
              <Typography variant="caption" sx={{ fontWeight: 500, maxWidth: '200px' }} noWrap>
                {displayContactName}
              </Typography>
            </Tooltip>
          </Box>
        </InfoRow>

        {/* Información del agente asignado si existe - Con tooltip del ID */}
        {ticket.assigned_to_agent && (
          <InfoRow>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <AssignmentIndIcon sx={{ fontSize: 10, color: 'secondary.main', mr: 0.25 }} />
              <Tooltip title={`ID: ${ticket.assigned_to_agent || 'N/A'}`}>
                <Typography variant="caption" sx={{ maxWidth: '200px' }} noWrap>
                  {agentName}
                </Typography>
              </Tooltip>
            </Box>
          </InfoRow>
        )}
      </InfoSection>
    );
  };

  // Determinar si el arrastre debe estar deshabilitado
  const isDragDisabled = column && column.boardType === 'flujo_bot';

  // Wrapper para el componente Draggable para manejar posibles errores
  try {
    return (
      <>
        <Draggable draggableId={ticketId} index={index} isDragDisabled={isDragDisabled}>
          {(provided, snapshot) => {
            try {
              return (
                <TicketContainer
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  elevation={snapshot.isDragging ? 4 : 1}
                  sx={{
                    transform: snapshot.isDragging ? 'rotate(3deg)' : 'none',
                    cursor: isDragDisabled ? 'default' : 'pointer'
                  }}
                  onClick={handleOpenModal}
                >
                  {/* Reorganizamos para que el título siempre sea visible */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    {/* Título del ticket - Ahora a la izquierda */}
                    <Typography
                      variant="subtitle2"
                      fontWeight="500"
                      color="primary"
                      sx={{
                        flexGrow: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.2,
                        mr: 1,
                      }}
                    >
                      {ticketTitle}
                    </Typography>

                    {/* Chip de prioridad - Ahora a la derecha */}
                    <PriorityChip
                      priority={ticketPriority}
                      label={ticketPriority.charAt(0).toUpperCase() + ticketPriority.slice(1)}
                      size="small"
                    />
                  </Box>

                  {/* Descripción del ticket (limitada) - Solo si hay espacio */}
                  {ticketDescription && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        mb: 0.5,
                        maxHeight: '20px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {ticketDescription}
                    </Typography>
                  )}

                  {renderTicketInfo()}
                </TicketContainer>
              );
            } catch (renderError) {
              console.error('Error al renderizar contenido de Draggable:', renderError);
              return (
                <TicketContainer elevation={1} sx={{ bgcolor: '#fff3e0' }}>
                  <Typography variant="body2" color="warning.main">
                    Error de renderizado
                  </Typography>
                </TicketContainer>
              );
            }
          }}
        </Draggable>

        {/* Modal de detalles del ticket */}
        <TicketDetailModal
          open={modalOpen}
          onClose={handleCloseModal}
          ticket={ticket}
          column={column}
        />
      </>
    );
  } catch (draggableError) {
    console.error('Error al renderizar Draggable:', draggableError, 'Ticket:', ticket);
    return (
      <TicketContainer elevation={1} sx={{ bgcolor: '#ffebee' }}>
        <Typography variant="body2" color="error">
          Error: No se puede arrastrar
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {ticketTitle}
        </Typography>
      </TicketContainer>
    );
  }
};

export default KanbanTicket;